import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AprilTag } from '../../../../ts/AprilTag';
import {
    ForkliftScanSession,
    startForkliftScanSession,
} from '../../../../utils/aprilTagDetector';

enum ScannerStatus {
    Starting = 'starting',
    Scanning = 'scanning',
    Error = 'error',
}

interface ForkliftTagScannerProps {
    active: boolean;
    forkliftList: AprilTag[];
    onDetected: (forklift: AprilTag) => void;
}

const ForkliftTagScanner: React.FC<ForkliftTagScannerProps> = ({
    active,
    forkliftList,
    onDetected,
}) => {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const sessionRef = useRef<ForkliftScanSession | null>(null);

    const [status, setStatus] = useState<ScannerStatus>(ScannerStatus.Starting);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [notRecognized, setNotRecognized] = useState<boolean>(false);

    // Keep the latest props available to the long-lived camera effect without
    // making them reactive deps, which would restart the camera on every render.
    const onDetectedRef = useRef(onDetected);
    const forkliftListRef = useRef(forkliftList);
    const tRef = useRef(t);
    useEffect(() => {
        onDetectedRef.current = onDetected;
        forkliftListRef.current = forkliftList;
        tRef.current = t;
    });

    useEffect(() => {
        if (!active) {
            return;
        }

        let cancelled = false;
        setStatus(ScannerStatus.Starting);
        setErrorMessage('');
        setNotRecognized(false);

        const cleanup = () => {
            sessionRef.current?.stop();
            sessionRef.current = null;
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
        };

        const start = async () => {
            if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) {
                if (!cancelled) {
                    setStatus(ScannerStatus.Error);
                    setErrorMessage(
                        tRef.current('control.inputs.selectForkliftModal.scanner.insecure') ?? ''
                    );
                }
                return;
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                    audio: false,
                });

                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }
                streamRef.current = stream;

                const video = videoRef.current;
                if (!video) {
                    cleanup();
                    return;
                }
                video.srcObject = stream;
                await video.play();

                if (cancelled) {
                    cleanup();
                    return;
                }
                setStatus(ScannerStatus.Scanning);

                const session = await startForkliftScanSession({
                    video,
                    forkliftList: forkliftListRef.current,
                    onDetected: (forklift) => {
                        cleanup();
                        onDetectedRef.current(forklift);
                    },
                    onUnrecognizedChange: setNotRecognized,
                });

                if (cancelled) {
                    session.stop();
                    cleanup();
                    return;
                }
                sessionRef.current = session;
            } catch (error) {
                if (cancelled) {
                    return;
                }
                setStatus(ScannerStatus.Error);
                const isPermission =
                    error instanceof DOMException &&
                    (error.name === 'NotAllowedError' || error.name === 'SecurityError');
                setErrorMessage(
                    isPermission
                        ? tRef.current('control.inputs.selectForkliftModal.scanner.permissionDenied') ?? ''
                        : tRef.current('control.inputs.selectForkliftModal.scanner.cameraError') ?? ''
                );
            }
        };

        start();

        return () => {
            cancelled = true;
            cleanup();
        };
    }, [active]);

    if (!active) {
        return null;
    }

    return (
        <Box
            sx={{
                mt: 2,
                position: 'relative',
                width: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                backgroundColor: '#000',
                aspectRatio: '4 / 3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <video
                ref={videoRef}
                playsInline
                muted
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: status === ScannerStatus.Scanning ? 'block' : 'none',
                }}
            />

            {status === ScannerStatus.Scanning && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '55%',
                            aspectRatio: '1 / 1',
                            border: '3px solid rgba(255, 255, 255, 0.85)',
                            borderRadius: 1,
                            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 0,
                            right: 0,
                            textAlign: 'center',
                            px: 2,
                            fontWeight: notRecognized ? 600 : 400,
                            color: notRecognized ? '#ff5252' : '#fff',
                            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                        }}
                    >
                        {notRecognized
                            ? t('control.inputs.selectForkliftModal.scanner.notRecognized')
                            : t('control.inputs.selectForkliftModal.scanner.instructions')}
                    </Typography>
                </>
            )}

            {status === ScannerStatus.Starting && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={28} sx={{ color: '#fff' }} />
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                        {t('control.inputs.selectForkliftModal.scanner.starting')}
                    </Typography>
                </Box>
            )}

            {status === ScannerStatus.Error && (
                <Typography
                    variant="body2"
                    sx={{ color: '#fff', px: 3, textAlign: 'center' }}
                >
                    {errorMessage}
                </Typography>
            )}
        </Box>
    );
};

export default ForkliftTagScanner;
